import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "components/dialog";
import { Subscription, SubscriptionPlan, SubscriptionUpdateRequest } from "types";
import { subscriptionsApi, subscriptionPlansApi } from "api/models";
import { handleAxiosAndCaughtError, validateJson } from "utils";
import toast from "react-hot-toast";
import { CreditCard, Eye, EyeOff, Copy } from "lucide-react";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import PureDropDown from "components/pure-dropdown";
import { useTranslation } from "react-i18next";

// Helper functions for date conversion
const formatDateForInput = (isoDate: string): string => {
  return dayjs(isoDate).format("YYYY-MM-DDTHH:mm");
};

const formatDateForApi = (inputDate: string): string => {
  // Convert local datetime to UTC datetime that represents the same time
  // e.g., "2025-10-17T08:00" becomes "2025-10-17T08:00:00.000Z"
  const date = new Date(inputDate);
  return date.toISOString();
};

interface EditSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subscription: Subscription | null;
}

const EditSubscriptionDialog: React.FC<EditSubscriptionDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  subscription,
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [editForm, setEditForm] = useState<SubscriptionUpdateRequest>({
    name: "",
    start_date: "",
    end_date: "",
    configurations: {},
  });

  const [editFormText, setEditFormText] = useState({
    configurations: "{}",
  });

  const [jsonErrors, setJsonErrors] = useState({
    configurations: "",
  });

  const [showSubscriptionKey, setShowSubscriptionKey] = useState(false);

  const [originalData, setOriginalData] = useState({
    name: "",
    start_date: "",
    end_date: "",
    configurations: "{}",
    subscription_plan_id: "",
  });

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(false);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string>("");

  const [autoOnboard, setAutoOnboard] = useState<boolean>(false);
  const [onboardDetails, setOnboardDetails] = useState({
    organization_name: "",
    workspace_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });

  // Memoize subscription status calculation
  const subscriptionStatus = useMemo(() => {
    if (!subscription) return null;
    
    const isExpired = subscription.end_date && dayjs().isAfter(dayjs(subscription.end_date));
    
    if (isExpired) {
      return { text: t("subscriptions.status.expired"), class: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' };
    } else if (subscription.is_active) {
      return { text: t("subscriptions.status.active"), class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    } else {
      return { text: t("subscriptions.status.inactive"), class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    }
  }, [subscription, t]);

  const handleJsonChange = (field: 'configurations', value: string) => {
    setEditFormText(prev => ({ ...prev, [field]: value }));
    setJsonErrors(prev => ({ ...prev, [field]: validateJson(value) }));
  };

  const handleCopySubscriptionKey = async () => {
    try {
      await navigator.clipboard.writeText(subscription.subscription_key);
      toast.success(t("subscriptions.messages.copiedKey"));
    } catch (err) {
      toast.error(t("subscriptions.messages.copyKeyFailed"));
    }
  };


  const hasChanges = () => {
    return (
      editForm.name !== originalData.name ||
      editForm.start_date !== originalData.start_date ||
      editForm.end_date !== originalData.end_date ||
      editFormText.configurations !== originalData.configurations ||
      subscriptionPlanId !== originalData.subscription_plan_id
    );
  };

  // Initialize form when subscription changes
  useEffect(() => {
    if (subscription) {
      const configurationsText = JSON.stringify(subscription.configurations, null, 2);
      const formattedStartDate = formatDateForInput(subscription.start_date);
      const formattedEndDate = formatDateForInput(subscription.end_date);
      
      setEditForm({
        name: subscription.name,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        configurations: subscription.configurations,
      });
      setEditFormText({
        configurations: configurationsText,
      });
      setJsonErrors({
        configurations: "",
      });
      setOriginalData({
        name: subscription.name,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        configurations: configurationsText,
        subscription_plan_id: subscription.subscription_plan_id || "",
      });
      setSubscriptionPlanId(subscription.subscription_plan_id || "");
      setAutoOnboard(false);
      setOnboardDetails({
        organization_name: "",
        workspace_name: "",
        admin_name: "",
        admin_email: "",
        admin_password: "",
      });
    }
  }, [subscription]);

  // Load plans when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const response = await subscriptionPlansApi.getListManagement({ skip: 0, take: 100, is_active: true });
        setPlans(response.items || []);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    loadPlans();
  }, [isOpen]);

  const handleSave = async () => {
    if (!subscription) return;

    if (!editForm.name.trim()) {
      toast.error(t("subscriptions.messages.nameRequired"));
      return;
    }

    if (!editForm.start_date) {
      toast.error(t("subscriptions.messages.startDateRequired"));
      return;
    }

    if (!editForm.end_date) {
      toast.error(t("subscriptions.messages.endDateRequired"));
      return;
    }

    // Check for JSON validation errors
    if (jsonErrors.configurations) {
      toast.error(t("subscriptions.messages.fixJsonBeforeSaving"));
      return;
    }

    // Parse JSON text to objects
    let parsedConfigurations = {};
    
    try {
      parsedConfigurations = JSON.parse(editFormText.configurations);
    } catch (e) {
      toast.error(t("subscriptions.messages.invalidConfigurationsJson"));
      return;
    }

    // Validate onboarding details if enabled
    if (autoOnboard) {
      const emailRegex = /\S+@\S+\.\S+/;
      if (!onboardDetails.organization_name.trim() || !onboardDetails.workspace_name.trim() || !onboardDetails.admin_name.trim() || !onboardDetails.admin_email.trim() || !onboardDetails.admin_password.trim()) {
        toast.error(t("subscriptions.messages.fillAllOnboardingFields"));
        return;
      }
      if (!emailRegex.test(onboardDetails.admin_email)) {
        toast.error(t("subscriptions.messages.enterValidAdminEmail"));
        return;
      }
      if (onboardDetails.admin_password.length < 8) {
        toast.error(t("subscriptions.messages.passwordMinLength"));
        return;
      }
    }

    setIsUpdating(true);
    try {
      const formData = {
        name: editForm.name,
        start_date: formatDateForApi(editForm.start_date),
        end_date: formatDateForApi(editForm.end_date),
        configurations: parsedConfigurations,
        subscription_plan_id: subscriptionPlanId || undefined,
        is_auto_onboard: !!autoOnboard,
        ...(autoOnboard ? { onboard_details: onboardDetails } : {}),
      };
      await subscriptionsApi.update(subscription.id, formData);
      await onSuccess();
      toast.success(t("subscriptions.messages.updatedSuccessfully"));
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!subscription) return;
    
    setIsTogglingStatus(true);
    try {
      if (subscription.is_active) {
        await subscriptionsApi.deactivate(subscription.id);
        toast.success(t("subscriptions.messages.deactivatedSuccessfully"));
      } else {
        await subscriptionsApi.activate(subscription.id);
        toast.success(t("subscriptions.messages.activatedSuccessfully"));
      }
      await onSuccess();
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    if (subscription) {
      const configurationsText = JSON.stringify(subscription.configurations, null, 2);
      const formattedStartDate = formatDateForInput(subscription.start_date);
      const formattedEndDate = formatDateForInput(subscription.end_date);
      
      setEditForm({
        name: subscription.name,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        configurations: subscription.configurations,
      });
      setEditFormText({
        configurations: configurationsText,
      });
      setJsonErrors({
        configurations: "",
      });
      setOriginalData({
        name: subscription.name,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        configurations: configurationsText,
        subscription_plan_id: subscription.subscription_plan_id || "",
      });
      setSubscriptionPlanId(subscription.subscription_plan_id || "");
    }
    onClose();
  };

  if (!subscription) return null;

  return (
    <Dialog isOpen={isOpen} setIsOpen={handleClose} outsideClickDisabled={true} backdropHandlerDisabled={true}>
      <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
        <div className="border-b border-gray-200 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("subscriptions.dialog.editTitle")}
            </h2>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {t("subscriptions.dialog.editDescription")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptions.dialog.nameLabel")}
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t("subscriptions.enterSubscriptionName")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptions.dialog.startDateLabel")}
              </label>
              <input
                type="datetime-local"
                value={editForm.start_date}
                onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptions.dialog.endDateLabel")}
              </label>
              <input
                type="datetime-local"
                value={editForm.end_date}
                onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div>
            <PureDropDown
              label={t("subscriptions.subscriptionPlan")}
              placeholder={isLoadingPlans ? t("subscriptions.dialog.loadingPlans") : t("subscriptions.dialog.selectPlanOptional")}
              options={plans.map((p) => ({ value: p.id, label: p.name }))}
              value={subscriptionPlanId}
              onChange={(val) => setSubscriptionPlanId(typeof val === 'string' ? val : '')}
              multiple={false}
              showClearButton={true}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("common.status")}
              </label>
              <div className="mt-1">
                {subscriptionStatus && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscriptionStatus.class}`}>
                    {subscriptionStatus.text}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              id="is_auto_onboard_edit"
              type="checkbox"
              checked={!!autoOnboard}
              onChange={(e) => setAutoOnboard(e.target.checked)}
              className="h-5 w-5 rounded-md border-gray-300 accent-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="is_auto_onboard_edit" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptions.dialog.autoOnboardLabel")}
            </label>
          </div>

          {autoOnboard && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("subscriptions.dialog.organizationNameLabel")}</label>
                  <input
                    type="text"
                    value={onboardDetails.organization_name}
                    onChange={(e) => setOnboardDetails((prev) => ({ ...prev, organization_name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t("subscriptions.enterOrganizationName")}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("subscriptions.dialog.workspaceNameLabel")}</label>
                  <input
                    type="text"
                    value={onboardDetails.workspace_name}
                    onChange={(e) => setOnboardDetails((prev) => ({ ...prev, workspace_name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t("subscriptions.enterWorkspaceName")}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("subscriptions.dialog.adminNameLabel")}</label>
                  <input
                    type="text"
                    value={onboardDetails.admin_name}
                    onChange={(e) => setOnboardDetails((prev) => ({ ...prev, admin_name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t("subscriptions.enterAdminName")}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("subscriptions.dialog.adminEmailLabel")}</label>
                  <input
                    type="email"
                    value={onboardDetails.admin_email}
                    onChange={(e) => setOnboardDetails((prev) => ({ ...prev, admin_email: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t("subscriptions.enterAdminEmail")}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("subscriptions.dialog.adminPasswordLabel")}</label>
                <input
                  type="password"
                  value={onboardDetails.admin_password}
                  onChange={(e) => setOnboardDetails((prev) => ({ ...prev, admin_password: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder={t("subscriptions.enterAdminPassword")}
                />
              </div>
            </>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptions.dialog.subscriptionKeyLabel")}
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type={showSubscriptionKey ? "text" : "password"}
                value={subscription.subscription_key}
                readOnly
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setShowSubscriptionKey(!showSubscriptionKey)}
                  className="flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors"
                >
                  {showSubscriptionKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCopySubscriptionKey}
                  className="flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptions.dialog.createdDate")}
              </label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {dayjs(subscription.created_at).format(DATE_TIME_FORMAT)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptions.dialog.updatedDate")}
              </label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {dayjs(subscription.updated_at).format(DATE_TIME_FORMAT)}
              </p>
            </div>
          </div>

           <div>
             <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
               {t("subscriptions.dialog.configurationsLabel")}
             </label>
             <textarea
               value={editFormText.configurations}
               onChange={(e) => handleJsonChange('configurations', e.target.value)}
               className={`mt-1 w-full rounded-lg border px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 dark:bg-gray-700 dark:text-white ${
                 jsonErrors.configurations 
                   ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50 dark:bg-red-900/10' 
                   : 'border-gray-300 focus:border-blue-500 dark:border-gray-600'
               }`}
               placeholder={t("subscriptions.dialog.configurationsPlaceholder")}
               rows={8}
             />
             {jsonErrors.configurations && (
               <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                 <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
                 {jsonErrors.configurations}
               </div>
             )}
             <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
               {t("subscriptions.dialog.configurationsHintEnterprise")}
             </div>
           </div>
        </div>

        <div className="flex gap-3 pt-3 sm:pt-4">
          {(() => {
            const isExpired = subscription.end_date && dayjs().isAfter(dayjs(subscription.end_date));
            
            if (isExpired) {
              return (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isUpdating || !editForm.name.trim() || !editForm.start_date || !editForm.end_date || !!jsonErrors.configurations || !hasChanges()}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-500 disabled:hover:shadow-md"
                >
                  {isUpdating ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <span>{t("subscriptions.actions.saveChanges")}</span>
                  )}
                </button>
              );
            }
            
            if (subscription.is_active) {
              return (
                <>
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    disabled={isTogglingStatus}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-red-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-red-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-500 disabled:hover:shadow-md"
                  >
                    {isTogglingStatus ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <span>{t("subscriptions.actions.deactivate")}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isUpdating || !editForm.name.trim() || !editForm.start_date || !editForm.end_date || !!jsonErrors.configurations || !hasChanges()}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-500 disabled:hover:shadow-md"
                  >
                    {isUpdating ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <span>{t("subscriptions.actions.saveChanges")}</span>
                    )}
                  </button>
                </>
              );
            } else {
              return (
                <>
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    disabled={isTogglingStatus}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-green-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-green-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500 disabled:hover:shadow-md"
                  >
                    {isTogglingStatus ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <span>{t("subscriptions.actions.activate")}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isUpdating || !editForm.name.trim() || !editForm.start_date || !editForm.end_date || !!jsonErrors.configurations || !hasChanges()}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2.5 font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-500 disabled:hover:shadow-md"
                  >
                    {isUpdating ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <span>{t("subscriptions.actions.saveChanges")}</span>
                    )}
                  </button>
                </>
              );
            }
          })()}
        </div>
      </div>
    </Dialog>
  );
};

export default EditSubscriptionDialog;
